import { useAuth, selectUser, type Role } from "./store";

/**
 * Front-end RBAC mirror of the backend role hierarchy (08 문서 §1·§3 매트릭스).
 *
 * The backend is the single source of truth — every gate here is a *UX*
 * optimisation (hide what the user can't do, redirect away from routes they
 * can't reach). A determined caller who forges a request still hits the
 * server's own `HasMinRole` / site-scope checks. Never treat these helpers as
 * a security boundary.
 *
 *   super_admin(4) > org_admin(3) > safety_manager(2) > operator(1) / worker(0)
 */

export const ROLE_RANK: Record<Role, number> = {
  worker: 0,
  operator: 1,
  safety_manager: 2,
  org_admin: 3,
  super_admin: 4,
};

/** Ordered high→low — handy for role pickers / filters. */
export const ROLES_DESC: Role[] = [
  "super_admin",
  "org_admin",
  "safety_manager",
  "operator",
  "worker",
];

/** Display-label i18n keys (08 §1). Promoted here from AccountMenu so every
 * surface (account menu, admin table, role picker) reads the same names.
 * 렌더 시점에 `t(ROLE_LABEL_KEY[role])` 로 현지화한다. */
export const ROLE_LABEL_KEY: Record<Role, string> = {
  super_admin: "pages.adminUsers.role.super_admin",
  org_admin: "pages.adminUsers.role.org_admin",
  safety_manager: "pages.adminUsers.role.safety_manager",
  operator: "pages.adminUsers.role.operator",
  worker: "pages.adminUsers.role.worker",
};

/** Badge tone per role (08 §5-3). Colour + label double-encode so the signal
 * survives colour-blind modes. */
export type RoleBadgeTone = "neutral" | "info" | "warning" | "danger";
export const ROLE_BADGE_TONE: Record<Role, RoleBadgeTone> = {
  super_admin: "danger",
  org_admin: "warning",
  safety_manager: "info",
  operator: "neutral",
  worker: "neutral",
};

/** True when `role` sits at or above `min` in the hierarchy. */
export function hasMinRole(role: Role | null | undefined, min: Role): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

/**
 * Reactive gate — `useCan("safety_manager")` re-renders when the signed-in
 * user's role changes (e.g. after the AppShell `me` refresh promotes them).
 */
export function useCan(min: Role): boolean {
  const user = useAuth(selectUser);
  return hasMinRole(user?.role, min);
}

/**
 * Roles the given actor may grant — self and everything below, per the
 * "자기보다 낮거나 같은 역할만" rule (08 §3). org_admin may create org_admin
 * but never super_admin; the server enforces the same via `_guard_privileges`.
 */
export function assignableRoles(role: Role | null | undefined): Role[] {
  if (!role) return [];
  const rank = ROLE_RANK[role];
  return ROLES_DESC.filter((r) => ROLE_RANK[r] <= rank);
}
